/** Shared allMids lookup — kept free of fetch/client imports for unit tests. */

/** REST allMids is a flat map; WS-shaped `{ mids: { … } }` also shows up. */
export function unwrapAllMidsPayload(raw: unknown): Record<string, string | number> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const obj = raw as Record<string, unknown>;
  if (obj.mids && typeof obj.mids === 'object' && !Array.isArray(obj.mids)) {
    return obj.mids as Record<string, string | number>;
  }
  return obj as Record<string, string | number>;
}

const PARAGON_MID_COINS = new Set(['TOTAL2', 'OTHERS', 'BTCD']);

/** Which allMids stream + symbol to query for a market id or HL coin. */
export function coinToAllMidsQuery(coin: string): { dex: string; sym: string } {
  const raw = String(coin || '').trim();
  const colon = raw.indexOf(':');
  if (colon > 0) {
    return {
      dex: raw.slice(0, colon).toLowerCase(),
      sym: raw
        .slice(colon + 1)
        .toUpperCase()
        .replace(/-PERP$/i, '')
        .replace(/-(USDT0|USDH|USDE|USDC|USDT|USD)$/i, ''),
    };
  }
  const u = raw.toUpperCase().replace(/-PERP$/i, '');
  const dash = u.lastIndexOf('-');
  if (dash > 0) {
    const base = u.slice(0, dash);
    const quote = u.slice(dash + 1);
    if (quote === 'USDT' || quote === 'USDT0') return { dex: 'cash', sym: base };
    if (quote === 'USDH' || quote === 'USDE') return { dex: 'km', sym: base };
    if (quote === 'USDC') return { dex: PARAGON_MID_COINS.has(base) ? 'para' : 'xyz', sym: base };
    if (quote === 'USD') return { dex: '', sym: base };
  }
  return { dex: '', sym: u };
}

export function parseMidValue(v: unknown): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

/** Pick this coin's mid from a shared allMids map — never the first key in the payload. */
export function lookupMidInMids(
  data: Record<string, string | number>,
  coin: string,
): number {
  const { dex, sym } = coinToAllMidsQuery(coin);
  if (!sym) return NaN;
  const tryKeys = dex
    ? [
        `${dex}:${sym}`,
        `${dex.toUpperCase()}:${sym}`,
        `A:${dex.toUpperCase()}:${sym}`,
        `${sym}-${dex === 'cash' ? 'USDT' : dex === 'km' ? 'USDH' : 'USDC'}`,
        sym,
      ]
    : [sym, `${sym}-PERP`, `${sym}-USD`, String(coin || '').toUpperCase()];
  for (const k of tryKeys) {
    const n = parseMidValue(data[k] ?? data[k.toUpperCase()] ?? data[k.toLowerCase()]);
    if (Number.isFinite(n)) return n;
  }
  const wantDex = dex ? `${dex.toUpperCase()}:${sym}` : null;
  for (const [key, val] of Object.entries(data || {})) {
    const ku = key.toUpperCase();
    if (ku === sym || ku === `${sym}-PERP` || ku === `${sym}-USD` || (wantDex != null && ku === wantDex)) {
      const n = parseMidValue(val);
      if (Number.isFinite(n)) return n;
    }
  }
  return NaN;
}
